import React, { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import { useTranslation } from 'src/arb-wrapper';
import { type Locale } from 'src/store/generated/leela-api-generated';
import { type Template } from 'src/types/template.types';
import type { FormRhfControllerProps } from 'src/components/common/forms/types';
import { TemplateSelect } from 'src/components/campaigns/forms/TemplateSelect';

interface FormTemplatesSelectProps extends FormRhfControllerProps {
  selectedLocales: Locale[];
  templates: Template[];
  countryCode?: string;
}

export const FormTemplatesSelect: React.FC<FormTemplatesSelectProps> = ({
  control,
  countryCode,
  name,
  selectedLocales,
  templates,
}) => {
  const fieldName = useMemo(() => `${name}.benderTemplates`, [name]);

  const sortedTemplates: Template[] = useMemo(() => {
    return [...templates].sort((a, b) => a.title.localeCompare(b.title));
  }, [templates]);

  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
    rules: { required: true },
  });

  const handleAddTemplate = () => {
    append({
      assetType: undefined,
      templateId: undefined,
      config: undefined,
    });
  };

  const handleDeleteTemplate = (index: number) => {
    remove(index);
  };

  return (
    <Column>
      <Divider />

      <Row alignmentHorizontal="justify">
        <Heading level={4}>{t('form_templates_header')}</Heading>
        <Button
          data-testid="add-template-button"
          disabled={!selectedLocales?.length}
          size="small"
          type="tertiary"
          onClick={handleAddTemplate}
        >
          <Icon tokens={plusTokens} />
          {t('form_add_template')}
        </Button>
      </Row>

      <Row widths={['100%']}>
        <Column>
          {fields.length === 0 && (
            <Text data-testid="template-error-message">
              {t('empty_list_error_message')}
            </Text>
          )}
          {fields.map(({ id }, index) => (
            <TemplateRow
              control={control}
              countryCode={countryCode}
              fieldName={fieldName}
              index={index}
              key={id}
              sortedTemplates={sortedTemplates}
              onDelete={() => { handleDeleteTemplate(index); }}
            />
          ))}
        </Column>
      </Row>
    </Column>
  );
};

interface TemplateRowProps {
  control: FormRhfControllerProps['control'];
  countryCode?: string;
  fieldName: string;
  index: number;
  sortedTemplates: Template[];
  onDelete: () => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({
  control,
  countryCode,
  fieldName,
  index,
  sortedTemplates,
  onDelete,
}) => {
  const { t } = useTranslation();
  const basePath = `${fieldName}.${index}`;

  return (
    <Row alignmentVertical='top' widths={['90%', '10%']}>
      <Column spacing="none">
        <TemplateSelect
          control={control}
          countryCode={countryCode}
          index={index}
          label={t('form_bender_template_label')}
          name={basePath}
          templates={sortedTemplates}
          testId={`template-select-${index}`}
        />
      </Column>
      <Button
        aria-label={t('form_global_delete')}
        data-testid={`delete-template-button-${index}`}
        size="small"
        type="icon"
        onClick={onDelete}
      >
        <Icon tokens={trashTokens} />
      </Button>
    </Row>
  );
};
